import {
  createContext,
  use,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import {CounterLabel, Heading, IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import styles from './ForYouSubSection.module.css'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import type {ForYouSubSectionId, ForYouSubSectionState} from './pick-expanded-section'

interface SectionVisibilityContextValue {
  register: (kind?: SectionVisibilityRegistrationKind) => () => void
}

const SectionVisibilityContext = createContext<SectionVisibilityContextValue | null>(null)
export type SectionVisibilityRegistrationKind = 'visible-item' | 'loading-item'

/**
 * A collapsible section wrapper styled to match the pull request inbox section
 * headers: a chevron toggle, the title, and a count of the visible items.
 *
 * The header and count are shown only when at least one child has registered as
 * a confirmed visible item; loading placeholders still report their pending
 * state to the parent (via `onVisibilityChange`), but do not make a section
 * appear before real content is known. When no children are visible the section
 * is hidden unless focus is still inside it, preventing orphaned headings and
 * layout gaps without hiding a focused toggle during async updates.
 *
 * Children stay mounted while the section is collapsed (the body is hidden, not
 * unmounted) so their visibility registration keeps the count accurate and any
 * async work keeps running.
 *
 * Expansion is fully controlled by the parent: `expanded` sets the open state
 * and `onToggle` fires (with this section's `id`) when the user clicks the
 * chevron. The parent decides how to reconcile its priority-based recommendation
 * with manual toggles (see `ForYouSection`).
 *
 * `onVisibilityChange` is called with this section's `id` whenever its own
 * visibility changes, so the parent can track both resolved visible items and
 * loading placeholders across every section.
 */
export function ForYouSubSection({
  id,
  title,
  expanded,
  onToggle,
  onVisibilityChange,
  children,
}: PropsWithChildren<{
  id: ForYouSubSectionId
  title: string
  expanded: boolean
  onToggle: (id: ForYouSubSectionId, currentlyExpanded: boolean) => void
  onVisibilityChange?: (id: ForYouSubSectionId, state: ForYouSubSectionState) => void
}>) {
  const [visibleCount, setVisibleCount] = useState(0)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // Mirrors registration counts synchronously so a registration can notify the
  // parent right away, instead of using a separate effect to watch and
  // re-forward state.
  const countsRef = useRef({visibleItems: 0, loadingItems: 0})

  const updateCounts = useCallback(
    (kind: SectionVisibilityRegistrationKind, delta: 1 | -1) => {
      const counts = countsRef.current
      if (kind === 'visible-item') counts.visibleItems += delta
      else counts.loadingItems += delta

      setVisibleCount(counts.visibleItems)
      onVisibilityChange?.(id, {
        hasVisibleItems: counts.visibleItems > 0,
        hasLoadingItems: counts.loadingItems > 0,
      })
    },
    [id, onVisibilityChange],
  )

  const register = useCallback(
    (kind: SectionVisibilityRegistrationKind = 'visible-item') => {
      updateCounts(kind, 1)
      return () => updateCounts(kind, -1)
    },
    [updateCounts],
  )

  const ctx = useMemo(() => ({register}), [register])

  const handleToggle = useCallback(() => {
    onToggle(id, expanded)
    sendPullRequestAnalyticsEvent('overview.section_toggle', 'OVERVIEW_FOR_YOU_SECTION', {
      section: title,
      is_open: !expanded,
    })
  }, [id, expanded, onToggle, sendPullRequestAnalyticsEvent, title])

  const bodyId = useId()

  return (
    <SectionVisibilityContext value={ctx}>
      <section className={visibleCount === 0 ? styles.hiddenUnlessFocused : undefined}>
        <div className={styles.header}>
          <IconButton
            className={styles.collapseButton}
            icon={expanded ? ChevronDownIcon : ChevronRightIcon}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
            aria-expanded={expanded}
            aria-controls={bodyId}
            onClick={handleToggle}
            variant="invisible"
            size="small"
          />
          <Heading as="h3" variant="small" className={styles.heading}>
            {title}
          </Heading>
          <CounterLabel>{visibleCount}</CounterLabel>
        </div>
        {/* The body stays mounted while collapsed so children keep their
            visibility registration (and the count) accurate; `hidden` removes it
            from layout and the accessibility tree. */}
        <div id={bodyId} className={styles.body} hidden={!expanded}>
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul role="list" className={styles.list}>
            {children}
          </ul>
        </div>
      </section>
    </SectionVisibilityContext>
  )
}

/**
 * Call this hook from a component that renders visible or loading content to
 * register it with the nearest `ForYouSubSection`. The section heading is shown
 * whenever at least one child registers as a confirmed visible item.
 *
 * Consumers of ForYouItem should early return null if they have no content to show
 * as once the ForYouItem is rendered we know it is visible and should be registered
 *
 * @example
 * useRegisterAsVisible()
 */
export function useRegisterAsVisible(kind: SectionVisibilityRegistrationKind = 'visible-item') {
  const ctx = use(SectionVisibilityContext)
  useLayoutEffect(() => {
    if (!ctx) return
    return ctx.register(kind)
  }, [ctx, kind])
}
